/**
 * SectionGrid Component
 *
 * Left sidebar displaying:
 * - Upload dropzone (when session is active)
 * - Section cards with images and validation status
 * - Unassigned images list
 * - Fallback to uploaded images list (when no sections exist)
 */

import React from 'react';
import { Upload, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { HistoryItem, RoomSection } from '@/types';
import { SectionWithImages } from '@/utils/sectionStatus';
import SectionGridItem from '@/components/section/SectionGridItem';
import { AnalysisThumbnail } from '@/components/session/AnalysisThumbnail';

interface UploadedImage {
  id: string;
  file: File;
  preview: string;
  result?: any;
  isAnalyzing?: boolean;
  error?: string;
}

interface ValidationCounts {
  match: number;
  mismatch: number;
  missing: number;
  unexpected: number;
}

interface SectionGridProps {
  // Upload props
  isSessionCompleted: boolean;
  getRootProps: () => any;
  getInputProps: () => any;
  isDragActive: boolean;

  // Section data
  sections: RoomSection[];
  sectionsWithStatus: SectionWithImages[];
  selectedImageId: string | null;

  // Handlers
  handleSectionClick: (sectionId: number, images: HistoryItem[]) => void;
  handleEmptySectionClick: (sectionId: number) => void;
  loadAnalysisImage: (id: number) => Promise<string | null>;
  handleViewValidation: (sectionId: number, images: HistoryItem[]) => void;
  handleViewSectionDetails: (section: RoomSection) => void;

  // Unassigned images
  unassignedImages: HistoryItem[];
  setImageToAssign: (image: HistoryItem) => void;
  setShowDirectCreateSectionModal: (show: boolean) => void;
  onDeleteAnalysis?: (analysisId: number) => void;

  // Fallback data (when no sections)
  correctedUploadedImages: UploadedImage[];
  correctedAnalyses: HistoryItem[];
  setSelectedImageId: (id: string | null) => void;
  loadImage: (id: number) => Promise<string | null>;

  // Validation counts getter (optional)
  getValidationCounts?: (sectionId: number) => ValidationCounts | null;
}

export const SectionGrid: React.FC<SectionGridProps> = ({
  isSessionCompleted,
  getRootProps,
  getInputProps,
  isDragActive,
  sections,
  sectionsWithStatus,
  selectedImageId,
  handleSectionClick,
  handleEmptySectionClick,
  loadAnalysisImage,
  handleViewValidation,
  handleViewSectionDetails,
  unassignedImages,
  setImageToAssign,
  setShowDirectCreateSectionModal,
  onDeleteAnalysis,
  correctedUploadedImages,
  correctedAnalyses,
  setSelectedImageId,
  loadImage,
  getValidationCounts,
}) => {
  const { t } = useTranslation();

  return (
    <div className="w-1/6 bg-white rounded-lg shadow-sm border p-4 flex flex-col h-full min-h-0 overflow-visible">
      {/* Create New Section Upload Area */}
      {!isSessionCompleted && (
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-lg p-2.5 text-center cursor-pointer transition-colors mb-4 flex-shrink-0",
            isDragActive
              ? "border-primary-500 bg-primary-50"
              : "border-primary-300 hover:border-primary-400 hover:bg-primary-50/50",
          )}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center space-y-1">
            <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
              <Plus className="h-4 w-4 text-primary-600" />
            </div>
            <div className="text-xs">
              <span className="font-medium text-primary-700">
                {isDragActive ? "Drop to create" : "Create New Section"}
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                Drop or click to add
              </p>
            </div>
          </div>
        </div>
      )}

      {isSessionCompleted && (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-2 text-center mb-4 flex-shrink-0 bg-gray-50">
          <div className="flex flex-col items-center space-y-0.5">
            <Upload className="h-3 w-3 text-gray-400" />
            <div className="text-xs">
              <span className="font-medium text-gray-500">
                {t("session_details.session_completed_no_upload")}
              </span>
              <p className="text-xs text-gray-400">
                {t("session_details.no_uploads_allowed")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Section Grid */}
      <div className="space-y-3 flex-1 overflow-y-auto overflow-x-visible">
        {sections.length > 0 ? (
          <>
            {/* Section Boxes */}
            {sectionsWithStatus.map(({ section, images, status }) => (
              <SectionGridItem
                key={section.id}
                section={section}
                images={images}
                status={status}
                isSelected={selectedImageId ? images.some((img: HistoryItem) => `analysis-${img.id}` === selectedImageId) : false}
                onClick={() => handleSectionClick(section.id, images)}
                onEmptyClick={() => handleEmptySectionClick(section.id)}
                loadImage={loadAnalysisImage}
                onValidationClick={() => handleViewValidation(section.id, images)}
                onInfoClick={() => handleViewSectionDetails(section)}
                validationCounts={getValidationCounts?.(section.id) ?? null}
              />
            ))}

            {/* Unassigned Images Section (if any) */}
            {unassignedImages.length > 0 && (
              <div className="border-t-2 border-gray-200 pt-3 mt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  Unassigned Images ({unassignedImages.length})
                </div>
                <div className="space-y-2">
                  {unassignedImages.map((analysis) => (
                    <div
                      key={`unassigned-${analysis.id}`}
                      className={cn(
                        "relative rounded-lg overflow-hidden cursor-pointer transition-all border",
                        selectedImageId === `analysis-${analysis.id}`
                          ? "border-primary-500 shadow-lg"
                          : "border-gray-200 hover:border-gray-300",
                      )}
                    >
                      {/* Clickable thumbnail for viewing */}
                      <div
                        onClick={() => setSelectedImageId(`analysis-${analysis.id}`)}
                        className="p-2"
                      >
                        <AnalysisThumbnail
                          analysisId={analysis.id}
                          imageName={analysis.image_name}
                          totalDetections={analysis.total_detections}
                          timestamp={analysis.timestamp}
                          detections={analysis.detections || []}
                          loadImage={loadImage}
                          aspectRatio="aspect-video"
                          showBorder={false}
                          onDelete={onDeleteAnalysis}
                        />
                      </div>
                      {/* Create section button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageToAssign(analysis);
                          setShowDirectCreateSectionModal(true);
                        }}
                        className="w-full py-2 px-3 bg-primary-50 hover:bg-primary-100 border-t border-gray-200 text-xs text-primary-700 font-medium transition-colors"
                      >
                        Click to create section
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* When no sections exist, show unassigned images with option to create section */}
            {unassignedImages.length > 0 && (
              <div className="mb-3">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  Unassigned Images ({unassignedImages.length})
                </div>
                <div className="space-y-2">
                  {unassignedImages.map((analysis) => (
                    <div
                      key={`unassigned-${analysis.id}`}
                      className={cn(
                        "relative rounded-lg overflow-hidden cursor-pointer transition-all border",
                        selectedImageId === `analysis-${analysis.id}`
                          ? "border-primary-500 shadow-lg"
                          : "border-gray-200 hover:border-gray-300",
                      )}
                    >
                      {/* Clickable thumbnail for viewing */}
                      <div
                        onClick={() => setSelectedImageId(`analysis-${analysis.id}`)}
                        className="p-2"
                      >
                        <AnalysisThumbnail
                          analysisId={analysis.id}
                          imageName={analysis.image_name}
                          totalDetections={analysis.total_detections}
                          timestamp={analysis.timestamp}
                          detections={analysis.detections || []}
                          loadImage={loadImage}
                          aspectRatio="aspect-video"
                          showBorder={false}
                          onDelete={onDeleteAnalysis}
                        />
                      </div>
                      {/* Create section button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageToAssign(analysis);
                          setShowDirectCreateSectionModal(true);
                        }}
                        className="w-full py-2 px-3 bg-primary-50 hover:bg-primary-100 border-t border-gray-200 text-xs text-primary-700 font-medium transition-colors"
                      >
                        Click to create section
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SectionGrid;
